import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { signInWithEmailAndPassword, signOut as firebaseSignOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { fetchMfaStatus, clearDeviceToken } from '@/lib/adminMfa';

const AdminAuthContext = createContext(undefined);

/**
 * Anchor admin account. This mirrors isAdmin() in firestore.rules and
 * isAdminUser() in functions/. It is an identity, not a credential — the
 * password is never held in the app.
 */
const ADMIN_EMAIL = 'admin@yoursongrequest.com';

/** Admin status comes from the signed Firebase token, never from client state. */
const resolveAdminFromToken = async (user) => {
  if (!user) return false;
  try {
    const token = await user.getIdTokenResult(true);
    if (token?.claims?.admin === true) return true;
    return (user.email || '').toLowerCase() === ADMIN_EMAIL;
  } catch (error) {
    console.warn('Could not read admin claim:', error?.message || error);
    return false;
  }
};

export const AdminAuthProvider = ({ children }) => {
  const { toast } = useToast();
  const navigate = useNavigate();

  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [adminLoading, setAdminLoading] = useState(true);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  // null while unknown; otherwise { enrolled, mfaPassed, deviceTrusted }.
  const [mfaState, setMfaState] = useState(null);

  /**
   * Asks the server where this session stands on 2FA. This also silently renews
   * the session when the browser holds a valid 30-day device token, which is
   * what stops a trusted device being prompted again.
   */
  const refreshMfaState = useCallback(async () => {
    try {
      const status = await fetchMfaStatus();
      const next = {
        enrolled: status?.enrolled === true,
        mfaPassed: status?.mfaPassed === true,
        deviceTrusted: status?.deviceTrusted === true,
      };
      setMfaState(next);
      return next;
    } catch (error) {
      // Fail closed: if the check cannot run we must not hand out console access.
      console.warn('Could not check admin 2FA state:', error?.message || error);
      const next = { enrolled: true, mfaPassed: false, deviceTrusted: false, unavailable: true };
      setMfaState(next);
      return next;
    }
  }, []);

  // Session state follows Firebase Auth, so a refresh re-verifies against the
  // server rather than trusting a flag the user could set themselves.
  useEffect(() => {
    if (!auth) {
      setIsCheckingSession(false);
      setAdminLoading(false);
      return undefined;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const isAdmin = await resolveAdminFromToken(user);
      setIsAdminAuthenticated(isAdmin);
      if (isAdmin) {
        await refreshMfaState();
      } else {
        setMfaState(null);
      }
      setIsCheckingSession(false);
      setAdminLoading(false);
    });

    return unsubscribe;
  }, [refreshMfaState]);

  const adminSignIn = useCallback(async (email, password) => {
    setAdminLoading(true);

    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      const isAdmin = await resolveAdminFromToken(user);

      if (!isAdmin) {
        await firebaseSignOut(auth);
        setIsAdminAuthenticated(false);
        toast({
          variant: 'destructive',
          title: 'Access Denied',
          description: 'This account does not have admin access.',
        });
        return { error: 'Not an admin account' };
      }

      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('ysr_web_session_resume_logged', '1');
      }
      try {
        const { recordWebLoginEvent } = await import('@/lib/recordWebLoginEvent');
        await recordWebLoginEvent(user, '/admin-login', { eventType: 'password_login' });
      } catch (_) { /* non-blocking */ }

      setIsAdminAuthenticated(true);

      // The console renders the 2FA step itself, so route there either way and
      // let it decide between enrolment, a code prompt, or straight through.
      const mfa = await refreshMfaState();
      if (mfa?.enrolled && mfa?.mfaPassed) {
        toast({
          title: 'Admin Access Granted',
          description: 'Welcome to the Admin Console',
        });
      }
      navigate('/admin-console');
      return { success: true };
    } catch (error) {
      console.error('Admin sign in error:', error?.code || error);
      setIsAdminAuthenticated(false);
      toast({
        variant: 'destructive',
        title: 'Access Denied',
        description: 'Invalid admin credentials',
      });
      return { error: 'Invalid credentials' };
    } finally {
      setAdminLoading(false);
    }
  }, [toast, navigate, refreshMfaState]);

  const adminSignOut = useCallback(async () => {
    setIsAdminAuthenticated(false);
    setMfaState(null);

    try {
      await firebaseSignOut(auth);
    } catch (error) {
      console.warn('Firebase sign out error:', error?.message || error);
    }

    toast({
      title: 'Admin Session Ended',
      description: 'You have been signed out of the admin console',
    });

    navigate('/');
  }, [toast, navigate]);

  const forgetThisDevice = useCallback(() => {
    clearDeviceToken();
  }, []);

  const value = {
    isAdminAuthenticated,
    adminLoading,
    isCheckingSession,
    adminSignIn,
    adminSignOut,
    // 2FA: mfaState is null until checked. `needsMfaEnrollment` and
    // `needsMfaChallenge` are what the console gates its UI on.
    mfaState,
    needsMfaEnrollment: isAdminAuthenticated && mfaState !== null && !mfaState.enrolled,
    needsMfaChallenge: isAdminAuthenticated && mfaState !== null
      && mfaState.enrolled && !mfaState.mfaPassed,
    refreshMfaState,
    forgetThisDevice
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};
